import React, { useCallback, useReducer } from 'react'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import { Form, FormGroup, Label, ModalBody } from 'reactstrap'
import ErrorBoundary from '@/components/ErrorBoundary'
import { Input, NumericInput } from '@/components/inputs'
import { ConfirmFooter } from '@/dialogs/common/confirmFooter'
import { useLocalization } from '@/hooks/localization'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import context from '@/context'
import { DatePicker } from '@/components/DatePicker'

const actionTypes = {
  setKey: 'setKey',
  setDescription: 'setDescription',
  setLatinDescription: 'setLatinDescription',
  setValue: 'setValue',
  setValidFrom: 'setValidFrom',
  setValidTo: 'setValidTo',
  setServerError: 'setServerError'
}

const initialState = {
  item: {
    key: '',
    description: '',
    latinDescription: '',
    value: null,
    validFrom: null,
    validTo: null,
    enabled: true
  },
  categoryId: 0,
  forEdit: false,
  serverError: null
}

function reducer (state = { ...initialState }, action) {
  switch (action.type) {
    case actionTypes.setKey: return { ...state, item: { ...state.item, key: action.data } }
    case actionTypes.setDescription: return { ...state, item: { ...state.item, description: action.data } }
    case actionTypes.setLatinDescription: return { ...state, item: { ...state.item, latinDescription: action.data } }
    case actionTypes.setValue: return { ...state, item: { ...state.item, value: action.data } }
    case actionTypes.setValidFrom: return { ...state, item: { ...state.item, validFrom: action.data } }
    case actionTypes.setValidTo: return { ...state, item: { ...state.item, validTo: action.data } }
    case actionTypes.setServerError: return { ...state, serverError: action.data }
  }
}

const EditConstantDialog = React.memo(function EditConstantDialog ({ item, categoryId, onAction, ...props }) {
  const { localize } = useLocalization()

  const [state, dispatch] = useReducer(reducer, item
    ? { ...initialState, item: { ...item }, forEdit: true }
    : { ...initialState, item: { ...initialState.item, categoryId: categoryId }, forEdit: false })

  useDebouncedEffect(() => {
    context.api.reportConstantService.validateConstant({ ...state.item })
      .then(() => dispatch({ type: actionTypes.setServerError, data: {} }))
      .catch(r => {
        const error = r ? r.response ? r.response.data ? r.response.data.error : null : null : null

        dispatch({ type: actionTypes.setServerError, data: error })
      })
  }, [state.item], 300)

  const onKeyChange = useCallback(e => dispatch({ type: actionTypes.setKey, data: e.target.value }), [])
  const onValueChange = useCallback(e => dispatch({ type: actionTypes.setValue, data: e }), [])
  const onDescriptionChange = useCallback(e => dispatch({ type: actionTypes.setDescription, data: e.target.value }), [])
  const onLatinDescriptionChange = useCallback(e => dispatch({ type: actionTypes.setLatinDescription, data: e.target.value }), [])
  const onValidFromChange = useCallback(e => dispatch({ type: actionTypes.setValidFrom, data: e }), [])
  const onValidToChange = useCallback(e => dispatch({ type: actionTypes.setValidTo, data: e }), [])

  const onActionButton = useCallback(() => onAction({ ...state.item }), [onAction, state.item])

  return (
    <React.Fragment>
      <ValidationBoundary serverError={state.serverError}>
        <BasicHeader title='Common.AddConstant' {...props} />
        <ModalBody>
          <ErrorBoundary>
            <Form>
              <FormGroup>
                <Label>{localize('Common.Key')}</Label>
                <Validator name='key' value={state.item.key} required>
                  <Input defaultValue={state.item.key} onChange={onKeyChange} disabled={state.forEdit} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label>{localize('Common.Value')}</Label>
                <Validator name='value' value={state.item.value} required>
                  <NumericInput defaultValue={state.item.value} onChange={onValueChange} disabled={state.forEdit} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label>{localize('Common.Description')}</Label>
                <Validator name='description' value={state.item.description}>
                  <Input defaultValue={state.item.description} onChange={onDescriptionChange} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label>{localize('Common.LatinDescription')}</Label>
                <Validator name='latinDescription' value={state.item.latinDescription}>
                  <Input defaultValue={state.item.latinDescription} onChange={onLatinDescriptionChange} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label>{localize('Common.ValidFrom')}</Label>
                <Validator name='validFrom' value={state.item.validFrom} required>
                  <DatePicker value={state.item.validFrom} onChange={onValidFromChange} />
                </Validator>
              </FormGroup>
              <FormGroup>
                <Label>{localize('Common.ValidTo')}</Label>
                <Validator name='validTo' value={state.item.validTo} required>
                  <DatePicker value={state.item.validTo} onChange={onValidToChange} />
                </Validator>
              </FormGroup>
            </Form>
          </ErrorBoundary>
        </ModalBody>
        <ValidationState render={({ isValid, isServerValid }) =>
          <ConfirmFooter onAction={onActionButton} disabled={!isValid || !isServerValid} icon='save' {...props} />}
        />
      </ValidationBoundary>
    </React.Fragment>)
})

export default {
  component: EditConstantDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/editConstant.js